import React, { useEffect, useState, Fragment, memo } from "react";
import { Helmet } from "react-helmet";
import { fetchAuthSession } from "aws-amplify/auth";
import { withCustomAuthenticator } from "../components/customAuthenticator";
import { Link } from "react-router-dom";
import Tooltip from "@mui/material/Tooltip";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import {
  Button,
  Typography,
  CircularProgress,
  Box,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
} from "@mui/material";
import {
  CloudUpload as CloudUploadIcon,
  CloudDownload as CloudDownloadIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import "./Estimator.css";
import SlidingPanel from "../components/SlidingPanel";
import DragOrUploadFileBox from "../components/DragOrUploadFileBox";
import templateFile from "../public/FIFO_Calculator_Template.csv";
import { suggestColumnMappings } from "../utils/csvMapper";
import MappingSuggestionModal from "../components/MappingSuggestionModal";
import PreviewMappingModal from "../components/PreviewMappingModal";
import FieldMappingModal from "../components/FieldMappingModal";
// import BlockchainSync from "../components/BlockchainSync";

const fieldDescriptions = {
  Date: "The Date field, should be formatted 'mm/dd/YYYY'",
  "Asset Sent": "The Ticker for the asset leaving your wallet (e.g. BTC)",
  "Quantity Sent": "The quantity of the Asset Sent (Note: not the USD value)",
  "Asset Received": "The Ticker for the asset entering your wallet (e.g. BTC)",
  "Quantity Received":
    "The quantity of the Asset Received (Note: not the USD value)",
  "USD Value":
    "The USD value of the transaction before any fees (Note: do not include '$')",
  "Fee Asset":
    "The Ticker for the asset the fee is paid in (e.g. BTC) (Note: if USD, leave null)",
  "Fee Quantity":
    "The quantity of the Fee Asset (Note: not the USD value, if USD, leave null)",
  "Fee USD Value": "The USD value of the fee (Note: do not include '$')",
  Transaction:
    "Type of transaction being processed. One of: Trade, Airdrop, Staking, FailedTx, Gas",
};

const typeDescriptions = {
  Trade: {
    description:
      "Any value representing a Trade transaction, separated by commas (ex. Buy,Sell,Swap)",
  },
  Airdrop: {
    description:
      "Any value representing an Airdrop transaction, separated by commas",
  },
  Staking: {
    description:
      "Any value representing a Staking transaction, separated by commas",
  },
  FailedTx: {
    description:
      "Any value representing a Failed Transaction, separated by commas",
  },
  Gas: {
    description:
      "Any value representing a Gas fee paid, separated by commas. This only applies when Gas is its own row, not when it's a part of a fee for a Trade transaction",
  },
};

const SubscriptionInfo = memo(({ currentPlan, transactionCount }) => {
  const isPro = currentPlan === "Pro";

  return (
    <div className="subscription-info-container">
      <div className="subscription-info">
        {isPro ? (
          <>
            <span className="pro-badge">Pro</span>
            <p>
              Thank you for being a Pro member! You have full access to all
              features, including unlimited transactions and the ability to
              download report summaries.
            </p>
          </>
        ) : (
          <>
            <p>
              {transactionCount >= 3 ? (
                <>
                  You have reached the limit of 3 free uploads.{" "}
                  <Link to="/account?section=membership">Upgrade to Pro</Link>{" "}
                  for unlimited uploads and the ability to download report
                  summaries necessary for tax filing.
                </>
              ) : (
                <>
                  Free Plan: {transactionCount}/3 files uploaded in the given
                  year. Update to Pro for unlimited transactions and the ability
                  to download report summaries necessary for tax filing.{" "}
                  <Link to="/account?section=membership">
                    Learn about Pro benefits
                  </Link>
                </>
              )}
            </p>
          </>
        )}
      </div>
    </div>
  );
});

const Estimator = () => {
  const [file, setFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [estimate, setEstimate] = useState(null);
  const [presignedUrls, setPresignedUrls] = useState(null);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [mappings, setMappings] = useState(() =>
    Object.keys(fieldDescriptions).reduce((acc, key) => {
      acc[key] = { description: fieldDescriptions[key], mapping: key };
      return acc;
    }, {})
  );
  const [types, setTypes] = useState(() =>
    Object.keys(typeDescriptions).reduce((acc, key) => {
      acc[key] = {
        description: typeDescriptions[key].description,
        mapping: key,
      };
      return acc;
    }, {})
  );
  const [fiats, setFiats] = useState("USD,USD/STABLE");
  const [taxYear, setTaxYear] = useState(new Date().getFullYear() - 1);
  const [inputAccurate, setInputAccurate] = useState(false);
  const [userSub, setUserSub] = useState(null);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [transactionCount, setTransactionCount] = useState(0);
  const [isLoadingPlan, setIsLoadingPlan] = useState(true);
  const [timeStatus, setTimeStatus] = useState(null);
  const [calculatedTaxYear, setCalculatedTaxYear] = useState(null);
  const [transactionsFound, setTransactionsFound] = useState(null);
  const [importSource, setImportSource] = useState("custom");
  const [portfolioSummary, setPortfolioSummary] = useState(null);
  const [isLoadingPortfolio, setIsLoadingPortfolio] = useState(true);
  const [syncTransactions, setSyncTransactions] = useState(false);
  const [showMappingSuggestions, setShowMappingSuggestions] = useState(false);
  const [currentSuggestions, setCurrentSuggestions] = useState(null);
  const [currentUnmappedColumns, setCurrentUnmappedColumns] = useState([]);
  const [mappingWarnings, setMappingWarnings] = useState([]);
  const [showFieldMapping, setShowFieldMapping] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [showPreviewMapping, setShowPreviewMapping] = useState(false);

  useEffect(() => {
    try {
      setUserSub(
        JSON.parse(localStorage.getItem("session")).tokens.idToken.payload.sub
      );
    } catch (error) {
      fetchUserSub();
    }
  }, []);

  const fetchUserSub = async () => {
    const user = await fetchAuthSession({ forceRefresh: true });
    setUserSub(user.userSub);
    localStorage.setItem(
      "session",
      JSON.stringify({
        ...JSON.parse(localStorage.getItem("session")),
        tokens: {
          ...JSON.parse(localStorage.getItem("session")).tokens,
          idToken: {
            ...JSON.parse(localStorage.getItem("session")).tokens.idToken,
            payload: user,
          },
        },
      })
    );
  };

  useEffect(() => {
    if (userSub) {
      fetchSubscriptionStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSub]);

  const fetchSubscriptionStatus = async () => {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens.idToken.toString();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/getSubscriptionStatus`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch subscription status");
      }

      const data = await response.json();
      setCurrentPlan(data.planType === "pro" ? "Pro" : "Free");

      // Only fetch transaction count for free users
      if (data.planType !== "pro") {
        const session = await fetchAuthSession();
        const token = session.tokens.idToken.toString();
        const usageResponse = await fetch(
          `${process.env.REACT_APP_API_URL}/getTransactionsUsage`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (usageResponse.ok) {
          const usageData = await usageResponse.json();
          setTransactionCount(usageData.transactionCount);
        }
      }
    } catch (error) {
      console.error("Error fetching subscription:", error);
    } finally {
      setIsLoadingPlan(false);
    }
  };

  const handleTypeChange = (type, newValue) => {
    setTypes((prev) => ({
      ...prev,
      [type]: {
        description: prev[type].description,
        mapping: newValue,
      },
    }));
  };

  const handleFiatChange = (newValue) => {
    setFiats(newValue);
  };

  const handleTaxYearChange = (newValue) => {
    setTaxYear(parseInt(newValue));
  };

  const handleSelectFile = async (event) => {
    var selectedFile;
    if (event.type === "change") {
      selectedFile = event.target.files[0];
    } else if (event.type === "drop") {
      selectedFile = event.dataTransfer.files[0];
    }

    if (selectedFile) {
      setIsProcessing(true);
      setFile(selectedFile);

      try {
        const reader = new FileReader();
        reader.onload = (e) => {
          const text = e.target.result;
          const lines = text.split("\n").filter((line) => line.trim()); // Remove empty lines
          const headers = lines[0]
            .split(",")
            .map((header) => header.trim().replace(/^["']|["']$/g, ""));

          // Parse first 5 rows into objects
          const previewRows = lines.slice(1, 6).map((line) => {
            const values = line
              .split(",")
              .map((val) => val.trim().replace(/^["']|["']$/g, ""));
            return headers.reduce((obj, header, index) => {
              obj[header] = values[index] || "";
              return obj;
            }, {});
          });

          setPreviewData(previewRows);

          // Get suggestions and warnings
          const { suggestions, warnings, unmappedColumns } =
            suggestColumnMappings(headers);

          // Convert suggestions to format: { requiredField: { header: matchedHeader, confidence: score } }
          const formattedSuggestions = Object.keys(fieldDescriptions).reduce(
            (acc, field) => {
              const suggestion = suggestions[field] || {
                header: null,
                confidence: 0,
              };
              acc[field] = {
                ...suggestion,
                isOriginalSuggestion: true,
                originalSuggestion: {
                  ...suggestion,
                  isOriginalSuggestion: true,
                },
              };
              return acc;
            },
            {}
          );

          setCurrentSuggestions(formattedSuggestions);
          setCurrentUnmappedColumns(unmappedColumns);
          setMappingWarnings(warnings);
          setShowMappingSuggestions(true);
        };

        reader.readAsText(selectedFile);
      } catch (error) {
        console.error("Error reading CSV header:", error);
        setErrorMessage("Error reading CSV file header");
      }

      setIsProcessing(false);
    }
  };

  const handleApplySuggestions = () => {
    const unmappedFields = Object.entries(currentSuggestions)
      .filter(([_, value]) => !value.header)
      .map(([field]) => field);

    if (unmappedFields.length > 0) {
      setMappingWarnings([
        `Please map all required fields. Missing: ${unmappedFields.join(", ")}`,
        ...mappingWarnings.filter(
          (w) => !w.startsWith("Please map all required fields")
        ),
      ]);
      return;
    }

    // Update mappings with the current suggestions
    const simplifiedMappings = Object.entries(currentSuggestions).reduce(
      (acc, [field, value]) => {
        acc[field] = {
          description: mappings[field].description,
          mapping: value.header,
        };
        return acc;
      },
      {}
    );

    setMappings((prevMappings) => ({
      ...prevMappings,
      ...simplifiedMappings,
    }));

    setShowMappingSuggestions(false);
    setShowFieldMapping(true);
  };

  const handleUpdateMapping = (field, newHeader) => {
    setCurrentSuggestions((prev) => {
      const originalSuggestion = prev[field].originalSuggestion || prev[field];

      const updated = {
        ...prev,
        [field]: {
          // If changing back to original header, restore original suggestion data
          ...(newHeader === originalSuggestion.header
            ? originalSuggestion
            : {
                header: newHeader, // Can now be null/None
                confidence: newHeader ? 1 : 0,
                matchType: "custom",
                isOriginalSuggestion: false,
                originalSuggestion: originalSuggestion.isOriginalSuggestion
                  ? originalSuggestion
                  : prev[field].originalSuggestion,
              }),
        },
      };

      // Update unmapped columns list
      const usedHeaders = Object.values(updated)
        .map((v) => v.header)
        .filter(Boolean); // Filter out null/None values

      const newUnmappedColumns = currentUnmappedColumns.filter(
        (col) => !usedHeaders.includes(col)
      );

      if (prev[field]?.header && !usedHeaders.includes(prev[field].header)) {
        newUnmappedColumns.push(prev[field].header);
      }

      setCurrentUnmappedColumns(newUnmappedColumns);
      return updated;
    });
  };

  const handleCloseMappings = () => {
    setShowMappingSuggestions(false);
  };

  const handleBackToColumnMapping = () => {
    setShowFieldMapping(false);
    setShowMappingSuggestions(true);
  };

  const handleFieldMappingConfirm = () => {
    setShowFieldMapping(false);
    setShowPreviewMapping(true);
  };

  const renderResults = () => {
    if (!estimate) {
      if (resultsLoading && !isProcessing) {
        return (
          <div className="results-section">
            <Typography>File accepted! Processing...</Typography>
            <CircularProgress size={24} color="inherit" />
          </div>
        );
      }
      return null;
    }

    return (
      <div className="results-section">
        <h4 className="results-title">Generated Results</h4>
        <h6 className="results-subtitle">
          Transactions Found in {calculatedTaxYear}: {transactionsFound}
        </h6>
        <div className="results-card">
          <div className="result-row">
            <span className="result-label">Long-Term Cost Basis:</span>
            <span className="result-value">
              $
              {formatNumberWithCommas(
                estimate["Long Term Cost Basis"].toFixed(2)
              )}
            </span>
          </div>
          <div className="result-row">
            <span className="result-label">Long-Term Proceeds:</span>
            <div>
              <span className="result-value">
                $
                {formatNumberWithCommas(
                  estimate["Long Term Proceeds"].toFixed(2)
                )}
              </span>
              <span
                className={`result-difference ${
                  estimate["Long Term Proceeds"] -
                    estimate["Long Term Cost Basis"] >=
                  0
                    ? "positive"
                    : "negative"
                }`}
              >
                {estimate["Long Term Proceeds"] -
                  estimate["Long Term Cost Basis"] >=
                0
                  ? " +"
                  : " "}
                {formatNumberWithCommas(
                  (
                    estimate["Long Term Proceeds"] -
                    estimate["Long Term Cost Basis"]
                  ).toFixed(2)
                )}
              </span>
            </div>
          </div>
          <div className="result-row">
            <span className="result-label">Short-Term Cost Basis:</span>
            <span className="result-value">
              $
              {formatNumberWithCommas(
                estimate["Short Term Cost Basis"].toFixed(2)
              )}
            </span>
          </div>
          <div className="result-row">
            <span className="result-label">Short-Term Proceeds:</span>
            <div>
              <span className="result-value">
                $
                {formatNumberWithCommas(
                  estimate["Short Term Proceeds"].toFixed(2)
                )}
              </span>
              <span
                className={`result-difference ${
                  estimate["Short Term Proceeds"] -
                    estimate["Short Term Cost Basis"] >=
                  0
                    ? "positive"
                    : "negative"
                }`}
              >
                {estimate["Short Term Proceeds"] -
                  estimate["Short Term Cost Basis"] >=
                0
                  ? " +"
                  : " "}
                {formatNumberWithCommas(
                  (
                    estimate["Short Term Proceeds"] -
                    estimate["Short Term Cost Basis"]
                  ).toFixed(2)
                )}
              </span>
            </div>
          </div>
          <div className="result-row">
            <span className="result-label">Income Proceeds:</span>
            <span className="result-value">
              $
              {formatNumberWithCommas(
                estimate["Total Income Tax Liability"].toFixed(2)
              )}
            </span>
          </div>
        </div>
      </div>
    );
  };

  const renderDownloadButtons = () => {
    if (!presignedUrls) return null;

    const files = [
      // { name: "main_report.csv", url: presignedUrls.main_report_file },
      // { name: "income_report.csv", url: presignedUrls.income_file },
      // {
      //   name: "capital_gains_report.csv",
      //   url: presignedUrls.capital_gains_file,
      // },
      // {
      //   name: "form_8949.pdf",
      //   url: presignedUrls.form_8949,
      // },
      {
        name: `Coin_Basis_crypto_tax_forms_${calculatedTaxYear}.zip`,
        url: presignedUrls.tax_report_zip,
      },
    ];

    if (currentPlan === "Pro") {
      return (
        <div className="download-section">
          {files.map((file) => (
            <Button
              key={file.name}
              className="download-button"
              startIcon={<CloudDownloadIcon />}
              onClick={() => (window.location.href = file.url)}
              disabled={isProcessing}
            >
              {file.name}
            </Button>
          ))}
        </div>
      );
    } else {
      return (
        <div className="blurred-buttons">
          <div className="blur-overlay">
            <p>
              Upgrade to Pro to download summaries.{" "}
              <Link to="/account?section=membership">Learn more</Link>
            </p>
          </div>
          <div className="download-section">
            {files.map((file) => (
              <Button
                key={file.name}
                className="download-button"
                startIcon={<CloudDownloadIcon />}
                onClick={() => (window.location.href = file.url)}
                disabled
              >
                {file.name}
              </Button>
            ))}
          </div>
        </div>
      );
    }
  };

  const checkResults = async (s3FolderPrefix, retries) => {
    const timeout = setTimeout(() => {
      setTimeStatus(
        "This seems to be taking a bit longer than expected. Please wait or contact support if issue persists"
      );
    }, 20000);
    const session = await fetchAuthSession();
    const token = session.tokens.idToken.toString();
    const response = await fetch(
      `${
        process.env.REACT_APP_API_URL
      }/checkForResults?folder=${encodeURIComponent(s3FolderPrefix)}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    ); // API endpoint that checks S3
    const data = await response.json();
    clearTimeout(timeout);

    if (!data.message) {
      const { metadata, ...results } = data;

      const sums = metadata["sums"];
      const warnings = metadata["warnings"];
      const calculatedTaxYear = metadata["tax_year"];
      const transactionsFound = metadata["transactions_found"];

      // Access the presigned URLs and display or download the files
      setPresignedUrls(results);
      setEstimate(sums);
      setWarnings(warnings);
      setResultsLoading(false);
      setTimeStatus(null);
      setTransactionCount(transactionCount + 1);
      setCalculatedTaxYear(calculatedTaxYear);
      setTransactionsFound(transactionsFound);
    } else {
      setErrorMessage(
        "Unknown Error. Please try again or contact support if issue persists"
      );
    }
  };

  const handleFileUpload = async () => {
    const selectedFile = file;
    if (selectedFile) {
      setIsProcessing(true);

      const filePrefix = `${userSub}/${
        new Date()
          .toISOString()
          .replace(/[-:T]/g, "")
          .replace(".", "")
          .split("Z")[0]
      }`;

      try {
        // Get the pre-signed URL from your server (API Gateway -> Lambda)
        const session = await fetchAuthSession();
        const token = session.tokens.idToken.toString();
        const response = await fetch(process.env.REACT_APP_API_URL, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            fileType: selectedFile.type,
            filePrefix: filePrefix,
            mappings: mappings,
            fiats: fiats,
            types: types,
            taxYear: taxYear,
          }),
        });
        const responseData = await response.json();

        // Check both response status and body status
        if (response.status === 403 || responseData.statusCode === 403) {
          throw new Error(JSON.parse(responseData.body).error);
        }
        if (responseData.statusCode === 401) {
          throw new Error("Unauthorized");
        }
        if (!response.ok) {
          throw new Error(
            "Failed to upload file, status code: " + response.status
          );
        }
        const url = responseData.url;

        // Upload the file to S3 using the pre-signed URL
        await fetch(url, {
          method: "PUT",
          body: selectedFile,
        });

        setIsProcessing(false);
        setResultsLoading(true);
      } catch (error) {
        setIsProcessing(false);
        setResultsLoading(false);
        setErrorMessage(`Error Uploading File: ${error}`);
        console.error("Error uploading file:", error);
      }
    }
  };

  const handlePortfolioUpload = async () => {
    const filePrefix = `${userSub}/${
      new Date()
        .toISOString()
        .replace(/[-:T]/g, "")
        .replace(".", "")
        .split("Z")[0]
    }`;
    try {
      const session = await fetchAuthSession();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/createPortfolioFile`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${session.tokens.idToken.toString()}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            filePrefix: filePrefix,
            taxYear: taxYear,
            fiats: fiats,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to create portfolio file");
      }
    } catch (error) {
      setErrorMessage("Error creating portfolio file: " + error.message);
      setIsProcessing(false);
      setResultsLoading(false);
      console.error("Error uploading portfolio:", error);
    }
  };

  const handleGenerateEstimate = async () => {
    if (currentPlan !== "Pro" && transactionCount >= 3) {
      setErrorMessage(
        "You've reached the free plan limit. Please upgrade to Pro to process more files."
      );
      return;
    }

    setIsProcessing(true);
    setEstimate(null);
    setWarnings([]);
    setPresignedUrls(null);
    setTransactionsFound(null);
    setCalculatedTaxYear(null);
    setErrorMessage(null);
    setResultsLoading(true);

    const ws = new WebSocket(
      `wss://7j9xbovwj3.execute-api.us-east-1.amazonaws.com/production/?userId=${userSub}`
    );

    ws.onopen = async () => {
      console.log("WebSocket connection established.");
      if (importSource === "custom") {
        handleFileUpload();
      } else {
        // Handle portfolio import
        setIsProcessing(false);
        handlePortfolioUpload();
      }
    };

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.message === "Results available") {
        console.log("Results are ready!");
        checkResults(data.folderId, 0);
        ws.close();
      } else {
        console.log("Error: ", data.message);
        setErrorMessage(data.message);
        setResultsLoading(false);

        ws.close();
      }
    };

    ws.onerror = (error) => {
      console.error("WebSocket error: ", error);
      setErrorMessage(error);
      setResultsLoading(false);
    };

    ws.onclose = () => {
      console.log("WebSocket connection closed.");
      setResultsLoading(false);
    };
  };

  function formatNumberWithCommas(number) {
    return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  }

  const fetchPortfolioSummary = async () => {
    try {
      setIsLoadingPortfolio(true);
      const session = await fetchAuthSession();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/getPortfolioSummary`,
        {
          headers: {
            Authorization: `Bearer ${session.tokens.idToken.toString()}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch portfolio summary");
      }

      const data = await response.json();
      setPortfolioSummary(data);
    } catch (error) {
      console.error("Error fetching portfolio summary:", error);
    } finally {
      setIsLoadingPortfolio(false);
    }
  };

  useEffect(() => {
    fetchPortfolioSummary();
  }, []);

  const renderMappingSummary = () => {
    return (
      <div className="mapping-summary">
        <Typography variant="h6" gutterBottom>
          Current Mappings
        </Typography>

        <Box sx={{ mb: 4 }}>
          <div className="mapping-section-header">
            <Typography variant="subtitle1" color="primary">
              Column Mappings
            </Typography>
            <Button
              size="small"
              startIcon={<EditIcon />}
              onClick={() => setShowMappingSuggestions(true)}
            >
              Edit Columns
            </Button>
          </div>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Required Field</TableCell>
                  <TableCell>Mapped Column</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(mappings).map(([field, value]) => (
                  <TableRow key={field}>
                    <TableCell>{field}</TableCell>
                    <TableCell>{value.mapping}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <Box sx={{ mb: 4 }}>
          <div className="mapping-section-header">
            <Typography variant="subtitle1" color="primary">
              Field Mappings
            </Typography>
            <Button
              size="small"
              startIcon={<EditIcon />}
              onClick={() => setShowFieldMapping(true)}
            >
              Edit Fields
            </Button>
          </div>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Type</TableCell>
                  <TableCell>Values</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>Fiat Currencies</TableCell>
                  <TableCell>{fiats}</TableCell>
                </TableRow>
                {Object.entries(types).map(([type, value]) => (
                  <TableRow key={type}>
                    <TableCell>{type}</TableCell>
                    <TableCell>{value.mapping}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </div>
    );
  };

  // Add function to check if all mappings are valid
  const areAllMappingsValid = () => {
    // Check if we have a file and headers to validate against
    if (!file || !currentUnmappedColumns || !mappings) return false;

    // Get all available columns from the file
    const availableColumns = [
      ...currentUnmappedColumns,
      ...Object.values(currentSuggestions || {})
        .map((s) => s?.header)
        .filter(Boolean),
    ];

    // Check if all required fields are mapped to existing columns
    const allColumnsMapped = Object.values(mappings).every(
      (mapping) =>
        mapping?.mapping && availableColumns.includes(mapping.mapping)
    );

    // Check if fiat and type mappings exist (they don't need column validation)
    const hasFiatMapping = fiats && fiats.trim() !== "";
    const allTypesMapped =
      types &&
      Object.values(types).every(
        (type) => type?.mapping && type.mapping.trim() !== ""
      );

    return allColumnsMapped && hasFiatMapping && allTypesMapped;
  };

  // Modify getMappingValidationMessage to only show for custom import
  const getMappingValidationMessage = () => {
    if (
      importSource !== "custom" ||
      !file ||
      !currentUnmappedColumns ||
      !mappings
    )
      return null;

    const missingMappings = [];

    // Get all available columns from the file
    const availableColumns = [
      ...currentUnmappedColumns,
      ...Object.values(currentSuggestions || {})
        .map((s) => s?.header)
        .filter(Boolean),
    ];

    Object.entries(mappings).forEach(([field, value]) => {
      if (!value?.mapping || !availableColumns.includes(value.mapping)) {
        missingMappings.push(
          `Column mapping for "${field}" (${
            value?.mapping || "not set"
          } is not a valid column in the file)`
        );
      }
    });

    // Check fiat mapping
    if (!fiats || fiats.trim() === "") {
      missingMappings.push("Fiat currency values");
    }

    // Check type mappings
    if (types) {
      Object.entries(types).forEach(([type, value]) => {
        if (!value?.mapping || value.mapping.trim() === "") {
          missingMappings.push(`Transaction type mapping for "${type}"`);
        }
      });
    }

    if (missingMappings.length === 0) return null;

    return `Please complete the following mappings before generating estimates:\n${missingMappings
      .map((m) => `• ${m}`)
      .join("\n")}`;
  };

  return (
    <div className="estimator-container">
      <Helmet>
        <title>Coin Basis: Tax Estimator</title>
        <meta property="og:title" content="Tax Estimator" />
        {/* Google Analytics */}
        <script
          async
          src="https://www.googletagmanager.com/gtag/js?id=G-1T6SHZQ9X8"
        ></script>
        <script>
          {`
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', 'G-1T6SHZQ9X8');
          `}
        </script>
        <script>
          {`
            (function(w,d,s,l,i){
              w[l]=w[l]||[];
              w[l].push({'gtm.start': new Date().getTime(), event:'gtm.js'});
              var f=d.getElementsByTagName(s)[0],
                  j=d.createElement(s), dl=l!='dataLayer'?'&l='+l:'';
              j.async=true;
              j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;
              f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer','GTM-MTZT7D3V');
          `}
        </script>
      </Helmet>
      <Navbar />
      <div className="estimator-content">
        {!isLoadingPlan && (
          <SubscriptionInfo
            currentPlan={currentPlan}
            transactionCount={transactionCount}
          />
        )}
        <h2 className="estimator-title">
          Coin Basis<span style={{ fontSize: 12 }}>&reg;</span> Tax Liability
          Estimator
        </h2>

        <div className="estimator-card">
          <h4 className="instructions-title">
            Please select your data source and follow the instructions below.
          </h4>

          <div className="source-selection">
            <h4 className="source-selection-title">Select Data Source</h4>
            <div className="source-options">
              <label className="source-option">
                <input
                  type="radio"
                  value="custom"
                  checked={importSource === "custom"}
                  onChange={(e) => setImportSource(e.target.value)}
                />
                <span className="source-label">Custom File Upload&nbsp;</span>
                <span className="source-description">
                  Upload and map your own transaction file
                </span>
              </label>

              <label
                className={`source-option ${
                  isLoadingPortfolio || !portfolioSummary?.transactionCount
                    ? "disabled"
                    : ""
                }`}
              >
                <input
                  type="radio"
                  value="portfolio"
                  checked={importSource === "portfolio"}
                  onChange={(e) => setImportSource(e.target.value)}
                  disabled={
                    isLoadingPortfolio || !portfolioSummary?.transactionCount
                  }
                />
                <span className="source-label">
                  Import from Coin Basis Portfolio&nbsp;
                </span>
                <span className="source-description">
                  {isLoadingPortfolio
                    ? "Loading portfolio details..."
                    : portfolioSummary?.transactionCount
                    ? `Use ${portfolioSummary.transactionCount} transactions from your portfolio`
                    : "No transactions found in portfolio"}
                </span>
              </label>
            </div>
          </div>

          {importSource === "custom" ? (
            <>
              <SlidingPanel
                buttonLabel="Show Instructions"
                cancelLabel="Hide Instructions"
                buttonClassName="panel-button"
              >
                <div className="instructions-content">
                  <Fragment>
                    Instructions
                    <br />
                    1. Prepare all of your transactions in a csv file. The
                    format should follow what is referenced in our{" "}
                    <a href={templateFile} download="coinbasis_template.csv">
                      template
                    </a>
                    . The accuracy of our cost basis calculations entirely
                    depends on the accuracy of the data you provide. For every
                    asset that you realized in 2024, you will need to include
                    the total history of transactions for that asset in order to
                    calculate the cost basis correctly.
                    <br />
                    <br />
                    2. Transaction Type should be one of the following: Trade,
                    Airdrop, Staking, FailedTx, Gas. A 'Trade' transaction is a
                    buy or sell of an asset. An 'Airdrop' transaction is a
                    distribution of an asset to your wallet. A 'Staking'
                    transaction is the staking reward received for an asset. A
                    'FailedTx' transaction is a transaction that did not
                    complete on the blockchain. A 'Gas' transaction is a fee
                    paid in a transaction that is not involved in buying or
                    selling. If there was gas or fees involved with buying or
                    selling, please include that in the 'Fee Asset' and 'Fee
                    Amount' columns.
                    <br />
                    <br />
                    3. Please specify any FIAT currencies used in your
                    transactions. FIAT currencies will not be calculated for
                    individual cost basis, however, those transactions are still
                    necessary for the overall tax liability calculation.
                    <br />
                    <br />
                    4. You must include the fair market USD value for each
                    transaction, even if the assets involved are not USD. For
                    example, if you swapped 1 ETH for 0.1 BTC, you must include
                    the USD value of that swap at the time of the transaction.
                    <br />
                    <br />
                    5. If you paid fees in a buy, sell, or swap transaction be
                    sure to include the fee asset and fee amount. For example,
                    if you bought 1 BTC for 10,000 USD and paid a 0.01 BTC fee,
                    you would include the fee asset as BTC, the fee quantity as
                    0.01 BTC, and the fee amount as the fair market USD value of
                    .01 BTC at the time. If you paid 10 USD as the fee, you
                    would include the fee asset as USD and the fee amount and
                    quantity as 10 USD.
                    <br />
                    <br />
                    6. Once you have your csv file ready, upload it here. If
                    your columns have different names than on our template, you
                    can map the columns to the appropriate fields using our tool
                    below.
                    <br />
                    <br />
                    7. After our calculations are complete, you will receieve 3
                    separate csv files: a main report, an income report, and a
                    capital gains report. The main report will include all of
                    your transactions and the cost basis calculations for each.
                    The income report will include all of your income
                    transactions and the tax liability for each. The capital
                    gains report will include all of your capital gains
                    transactions and the tax liability for each.
                    <br />
                    <br />
                    8. We cannot provide tax advice, but we can provide you with
                    the data you need when filing your taxes. You will need to
                    submit the income and capital gains reports to the IRS when
                    you file your taxes. Please be sure to verify the
                    information and consult with a tax professional.
                    <br />
                    <br />
                  </Fragment>
                </div>
              </SlidingPanel>

              <div className="upload-section">
                <h4 className="upload-title">Upload your transaction file</h4>
                <span className="upload-subtitle">
                  Unsure what it should look like? Download our{" "}
                  <a href={templateFile} download="coinbasis_template.csv">
                    template
                  </a>
                </span>

                <input
                  type="file"
                  accept=".csv"
                  id="file-input"
                  onChange={handleSelectFile}
                  style={{ display: "none" }}
                />
                <DragOrUploadFileBox onFileDrop={handleSelectFile}>
                  <label htmlFor="file-input">
                    <Button
                      className="upload-button"
                      startIcon={<CloudUploadIcon />}
                      component="span"
                      disabled={isProcessing || resultsLoading}
                    >
                      {file ? file.name : "Choose File"}
                    </Button>
                  </label>
                </DragOrUploadFileBox>
                <h4 className="mapping-title">
                  ⚠️ Please confirm your column mappings below are accurate to
                  ensure your file gets processed correctly. Please see the How
                  it Works page or contact support for assistance if needed.
                </h4>
                {file && renderMappingSummary()}
                {/* <div className="mapping-panel">
                  <SlidingPanel
                    buttonLabel="Advanced Settings &#9660;"
                    cancelLabel="Close Settings &#9650;"
                    resetText="Reset to Defaults"
                    onReset={handleMappingsReset}
                    disabled={resultsLoading}
                    buttonClassName="panel-button"
                  >
                    <MappingEditor
                      data={mappings}
                      onMappingChange={handleMappingChange}
                      fiats={fiats}
                      onFiatChange={handleFiatChange}
                      types={types}
                      onTypeChange={handleTypeChange}
                      taxYear={taxYear}
                      onTaxYearChange={handleTaxYearChange}
                    />
                  </SlidingPanel>
                </div> */}
                <div className="mapping-editor-section">
                  <div className="mapping-editor-section-title">Tax Year</div>
                  <div className="mapping-editor-subtitle">
                    Enter the tax year you would like to calculate tax liability
                    for
                  </div>
                  <select
                    value={taxYear}
                    onChange={(event) =>
                      handleTaxYearChange(event.target.value)
                    }
                    className="mapping-input"
                  >
                    {Array.from({ length: 25 }, (_, i) => (
                      <option key={i} value={new Date().getFullYear() - i}>
                        {new Date().getFullYear() - i}
                      </option>
                    ))}
                  </select>
                </div>

                <label className="accuracy-checkbox">
                  <input
                    type="checkbox"
                    checked={inputAccurate}
                    onChange={() => setInputAccurate(!inputAccurate)}
                  />
                  <p>
                    I agree to the{" "}
                    <Link to="/faq#terms-and-conditions">
                      Terms and Conditions
                    </Link>
                    , have verified my data is accurate, and my columns in my
                    uploaded CSV are properly mapped.
                  </p>
                </label>

                {importSource === "custom" && (
                  <label className="accuracy-checkbox">
                    <input
                      type="checkbox"
                      checked={syncTransactions}
                      onChange={() => setSyncTransactions(!syncTransactions)}
                      disabled={true}
                      style={{ opacity: 0.5, cursor: "not-allowed" }}
                    />
                    <p style={{ color: "#666" }}>
                      <span
                        style={{
                          backgroundColor: "#e0e0e0",
                          padding: "2px 6px",
                          borderRadius: "4px",
                          fontSize: "0.8em",
                          marginRight: "8px",
                          color: "#666",
                        }}
                      >
                        COMING SOON
                      </span>
                      Sync my uploaded transactions with my portfolio
                    </p>
                  </label>
                )}
              </div>
            </>
          ) : (
            <div className="portfolio-import-section">
              <div className="mapping-editor-section">
                <div className="mapping-editor-section-title">
                  Field Mappings (no extra spaces)
                </div>
                <div className="mapping-editor-subtitle">
                  Let us know how your transactions are labeled in your
                  portfolio
                </div>
                <div className="mapping-row">
                  <Tooltip
                    title="Comma separated list of terms used to identify currencies that you do not want considered as cryptocurrency assets for tax purposes (e.g. USD,USD/Stable)"
                    placement="top"
                    arrow
                  >
                    <div className="mapping-label">Fiat Values:</div>
                  </Tooltip>
                  <input
                    type="text"
                    value={fiats}
                    onChange={(event) => setFiats(event.target.value)}
                    className="mapping-input"
                  />
                </div>
              </div>
              <div className="mapping-editor-section">
                <div className="mapping-editor-section-title">Tax Year</div>
                <div className="mapping-editor-subtitle">
                  Enter the tax year you would like to calculate tax liability
                  for
                </div>
                <select
                  value={taxYear}
                  onChange={(event) => handleTaxYearChange(event.target.value)}
                  className="mapping-input"
                >
                  {Array.from({ length: 25 }, (_, i) => (
                    <option key={i} value={new Date().getFullYear() - i}>
                      {new Date().getFullYear() - i}
                    </option>
                  ))}
                </select>
              </div>

              <label className="accuracy-checkbox">
                <input
                  type="checkbox"
                  checked={inputAccurate}
                  onChange={() => setInputAccurate(!inputAccurate)}
                />
                <p>
                  I agree to the{" "}
                  <Link to="/faq#terms-and-conditions">
                    Terms and Conditions
                  </Link>{" "}
                  and verify that my portfolio data is accurate and complete.
                </p>
              </label>
            </div>
          )}
          <div className="upload-section">
            {file && getMappingValidationMessage() && (
              <div className="mapping-validation-message">
                <Typography
                  variant="body2"
                  color="error"
                  style={{ whiteSpace: "pre-line", marginBottom: "16px" }}
                >
                  {getMappingValidationMessage()}
                </Typography>
              </div>
            )}

            <Button
              className="generate-button"
              onClick={handleGenerateEstimate}
              disabled={
                isProcessing ||
                !file ||
                resultsLoading ||
                !inputAccurate ||
                (currentPlan !== "Pro" && transactionCount >= 3) ||
                (importSource === "custom" && !areAllMappingsValid())
              }
            >
              {isProcessing ? (
                <CircularProgress size={24} color="inherit" />
              ) : currentPlan !== "Pro" && transactionCount >= 3 ? (
                "Upgrade to Pro to process more files"
              ) : importSource === "custom" && !areAllMappingsValid() ? (
                "Complete Mappings to Generate Estimates"
              ) : (
                "Generate Estimates"
              )}
            </Button>
            {timeStatus && <div className="time-message">{timeStatus}</div>}
            {errorMessage && (
              <div className="error-message">{errorMessage}</div>
            )}

            {warnings.map((warn, index) => (
              <div key={index} className="warning-message">
                {warn}
              </div>
            ))}

            {renderResults()}
            {renderDownloadButtons()}
            <br />
            <br />
            <div className="description-section">
              <h3 className="description-title">
                What files are included in my download?
              </h3>
              <div className="description-content">
                <div className="description-card">
                  <h5>Form 8949</h5>
                  <p>
                    This file is the IRS issued form that you will need to file
                    your taxes. It comes prefilled with the summary data shown
                    above, which, when supplemented with the Capital Gains
                    report, is sufficient for reporting your gains/losses to the
                    IRS.
                  </p>
                </div>
                <div className="description-card">
                  <h5>Capital Gains Report</h5>
                  <p>
                    This file contains all of your capital gains transactions
                    and the tax liability for each.
                  </p>
                </div>
                <div className="description-card">
                  <h5>Income Report</h5>
                  <p>
                    This file contains all of your income transactions and the
                    tax liability for each.
                  </p>
                </div>
                <div className="description-card">
                  <h5>Main Report</h5>
                  <p>
                    This file contains all of your transactions and the cost
                    basis calculations for each.
                  </p>
                </div>
                <div className="description-card">
                  <h5>README file</h5>
                  <p>
                    An informational document that goes into more detail about
                    the calculations and data included in the other files.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="estimator-card">
          <h4 className="upload-title">Disclaimer</h4>
          <div className="instructions-content">
            This software is designed to assist users in calculating the cost
            basis for cryptocurrency transactions and generating reports for
            informational purposes only. It does not provide tax, legal, or
            financial advice and should not be used as a substitute for
            consulting with a licensed tax professional or accountant.
            <br />
            <br />
            The calculations and reports generated by this software are based
            solely on the data provided by the user and do not account for all
            potential tax implications, regulatory requirements, or individual
            circumstances. Users are responsible for verifying the accuracy of
            the input data and the resulting calculations before relying on them
            for tax reporting or filing.
            <br />
            <br />
            This software does not file taxes or communicate directly with tax
            authorities. It is the user's responsibility to review and file
            their taxes in accordance with applicable laws and regulations.
            <br />
            <br />
            By using this software, you acknowledge and agree that the
            developers and operators of this software are not liable for any
            errors, omissions, or damages resulting from the use of this
            software or its outputs.
          </div>
        </div>
      </div>
      <Footer />

      {showMappingSuggestions && (
        <MappingSuggestionModal
          open={showMappingSuggestions}
          onClose={handleCloseMappings}
          suggestions={currentSuggestions}
          unmappedColumns={currentUnmappedColumns}
          warnings={mappingWarnings}
          onApply={handleApplySuggestions}
          onUpdateMapping={handleUpdateMapping}
          fieldDescriptions={fieldDescriptions}
        />
      )}
      <FieldMappingModal
        open={showFieldMapping}
        onClose={() => setShowFieldMapping(false)}
        onBack={handleBackToColumnMapping}
        onConfirm={handleFieldMappingConfirm}
        fiats={fiats}
        onFiatChange={handleFiatChange}
        types={types}
        onTypeChange={handleTypeChange}
        typeDescriptions={typeDescriptions}
      />
      <PreviewMappingModal
        open={showPreviewMapping}
        onClose={() => setShowPreviewMapping(false)}
        onBack={() => {
          setShowPreviewMapping(false);
          setShowFieldMapping(true);
        }}
        mappings={mappings}
        data={previewData}
      />
    </div>
  );
};

export default withCustomAuthenticator(Estimator);
